import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, streamMessage, streamAutoProgress } from '../../lib/api-client';
import type { ChatMessage, ModelSelection, PageContext } from '../../lib/api-types';
import { initialStreamState, reduceStream, type StreamState } from '../../lib/stream-accumulator';
import { keys } from '../../lib/queries';
import { emailKey } from '../../lib/email-api';
import {
  applyLiveRecordCount,
  publishRecordCountIncrease,
  recordCountIncreaseFromToolResult,
} from '../../lib/record-count-events';
import type { SchemaDescriptor } from '../../lib/api-types';

const WORKSPACE_MUTATING_TOOLS = new Set([
  'create_record',
  'delete_record',
  'execute_import',
  'revert_import',
  'create_object',
  'delete_object',
  'create_field',
  'delete_field',
]);

/**
 * Drives the live viewer for one server-owned turn. Disposing this hook closes
 * only the browser transport; the durable turn continues on resonance-server.
 */
export function useChatStream(
  conversationId: string | null,
  onConversationSettled?: (conversationId: string) => void,
  pageContext?: PageContext,
  onConversationCreated?: (conversationId: string) => void,
) {
  const client = useQueryClient();
  const [pendingUserMessage, setPendingUserMessage] = useState<ChatMessage | null>(null);
  const [stream, setStream] = useState<StreamState | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const activeConversationIdRef = useRef<string | null>(null);
  const activeTurnIdRef = useRef<string | null>(null);
  const draftConversationIdRef = useRef<string | null>(null);
  const createDraftRef = useRef<Promise<string> | null>(null);

  const ensureConversation = useCallback(async (): Promise<string> => {
    if (conversationId) return conversationId;
    if (draftConversationIdRef.current) return draftConversationIdRef.current;

    createDraftRef.current ??= api.createConversation().then((result) => {
      draftConversationIdRef.current = result.conversation.id;
      onConversationCreated?.(result.conversation.id);
      return result.conversation.id;
    }).finally(() => {
      createDraftRef.current = null;
    });

    return createDraftRef.current;
  }, [conversationId, onConversationCreated]);

  const send = useCallback(
    async (text: string, model: ModelSelection, mode: 'chat' | 'progress' = 'chat') => {
      if (!text.trim() || abortRef.current) return;

      const controller = new AbortController();
      const turnId = crypto.randomUUID();
      abortRef.current = controller;
      activeTurnIdRef.current = turnId;

      setPendingUserMessage({
        id: `local-${Date.now()}`,
        role: 'user',
        text,
        parts: [{ type: 'text', text }],
        createdAt: new Date().toISOString(),
      });
      setStream(initialStreamState());

      let activeConversationId: string | null = null;
      let terminalError: string | undefined;

      try {
        activeConversationId = await ensureConversation();
        activeConversationIdRef.current = activeConversationId;
        const streamingConversationId = activeConversationId;
        await (mode === 'progress' ? streamAutoProgress : streamMessage)(
          streamingConversationId,
          {
            text,
            model,
            turnId,
            ...(pageContext ? { pageContext } : {}),
            signal: controller.signal,
          },
          (event) => {
            if (event.event === 'message_end' && event.data.status === 'error') terminalError = event.data.error ?? 'The request could not be completed.';
            if (event.event === 'message_start') {
              activeTurnIdRef.current = event.data.messageId;
              // Makes the working conversation visible in the persistent rail.
              void client.invalidateQueries({ queryKey: keys.conversations });
            }
            if (event.event === 'message_end' && event.data.conversation) {
              client.setQueryData(keys.conversation(streamingConversationId), {
                conversation: event.data.conversation,
              });
            }
            if (event.event === 'tool_result' && event.data.success &&
              ['compose_email', 'request_email_draft', 'edit_email_draft', 'manage_email_resources'].includes(event.data.toolName)) {
              void client.invalidateQueries({ queryKey: emailKey() });
            }
            if (
              event.event === 'tool_result'
              && event.data.success
              && WORKSPACE_MUTATING_TOOLS.has(event.data.toolName)
            ) {
              // A successful agent write is already committed when its tool
              // result arrives, so update CRM rows and rail counters now rather
              // than waiting for the rest of the answer to finish.
              const increase = recordCountIncreaseFromToolResult(event.data.toolName, event.data.output);
              if (increase) {
                client.setQueryData<SchemaDescriptor>(keys.schema, (current) => (
                  applyLiveRecordCount(current, increase)
                ));
                publishRecordCountIncrease(increase);
              }
              void client.invalidateQueries({ queryKey: keys.schema });
              void client.invalidateQueries({ queryKey: ['records'] });
              void client.invalidateQueries({ queryKey: ['timeline'] });
            }
            setStream((current) => reduceStream(current ?? initialStreamState(), event));
          },
        );
      } catch (error) {
        if (controller.signal.aborted) {
          // Navigation/unmount only closes this viewer. The server continues.
          return;
        }
        if (abortRef.current === controller) abortRef.current = null;
        if (activeConversationId) {
          await client.invalidateQueries({ queryKey: keys.conversation(activeConversationId) });
          await client.invalidateQueries({ queryKey: keys.conversations });
          onConversationSettled?.(activeConversationId);
        }
        setPendingUserMessage(null);
        setStream(current => ({ ...(current ?? initialStreamState()), status: 'error', error: error instanceof Error ? error.message : 'Couldn’t start this request. Please try again.' }));
        return;
      }

      if (abortRef.current === controller) abortRef.current = null;
      activeConversationIdRef.current = null;
      activeTurnIdRef.current = null;
      await client.invalidateQueries({ queryKey: keys.conversation(activeConversationId) });
      await client.invalidateQueries({ queryKey: keys.conversations });
      void client.invalidateQueries({ queryKey: ['records'] });
      void client.invalidateQueries({ queryKey: ['timeline'] });
      void client.invalidateQueries({ queryKey: keys.schema });
      void client.invalidateQueries({ queryKey: keys.dailyBrief });
      void client.invalidateQueries({ queryKey: keys.personalization });
      setPendingUserMessage(null);
      if (terminalError) setStream(current => ({ ...(current ?? initialStreamState()), status: 'error', error: terminalError! }));
      else setStream(null);
      onConversationSettled?.(activeConversationId);
    },
    [client, ensureConversation, onConversationSettled, pageContext],
  );

  const cancel = useCallback(() => {
    const activeConversationId = activeConversationIdRef.current;
    const activeTurnId = activeTurnIdRef.current;
    if (!activeConversationId || !activeTurnId) return;
    void api.cancelTurn(activeConversationId, activeTurnId).catch((error) => {
      setStream((current) => current ? {
        ...current,
        error: error instanceof Error ? error.message : 'The reply could not be stopped.',
      } : current);
    });
  }, []);

  const cancelPersistedTurn = useCallback(async (turnId: string) => {
    if (!conversationId) return;
    try {
      await api.cancelTurn(conversationId, turnId);
    } catch {
      // The conversation poll remains authoritative and keeps the Stop control
      // available for retry if the cancellation request did not arrive.
    }
    await client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
    await client.invalidateQueries({ queryKey: keys.conversations });
  }, [client, conversationId]);

  useEffect(() => () => {
    // This AbortController owns fetch only. Server cancellation is exclusively
    // the explicit endpoint called above.
    abortRef.current?.abort();
  }, []);

  return {
    send,
    ensureConversation,
    cancel,
    cancelPersistedTurn,
    pendingUserMessage,
    stream,
    isStreaming: stream?.status === 'streaming',
  };
}
